import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "@/lib/api";

const PREF_TOKEN_KEY = "pref_token";
const PREF_USER_KEY = "pref_user";
function getAuthHeader() { return { Authorization: `Bearer ${localStorage.getItem(PREF_TOKEN_KEY)}` }; }

export default function PrefStudentPortal() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem(PREF_USER_KEY) || "null");
    if (!u || u.role !== "STUDENT") { navigate("/engineering-admission/portal-login"); return; }
    setUser(u);
  }, [navigate]);

  const fetchDrafts = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get(`/preferences/drafts/${user.id}`, { headers: getAuthHeader() });
      setDrafts(res.data || []);
      if (res.data?.length > 0) setSelectedDraft(res.data[0]);
    } catch { toast.error("Failed to load preference lists"); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const handlePrint = () => {
    if (!selectedDraft || !selectedDraft.preferences?.length) { toast.error("No data to print"); return; }
    const prefs = selectedDraft.preferences;
    const listType = selectedDraft.list_type;
    const sn = user.name;
    const rn = user.registration_number || "";
    const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const rankLabel = listType === 'CET' ? 'Rank' : 'AI Merit';
    const statusText = selectedDraft.status === 'FINALIZED' ? 'Finalized' : 'Draft';
    const statusClass = selectedDraft.status === 'FINALIZED' ? 'finalized' : 'draft';
    
    const filters = selectedDraft.filters_used || {};
    const studentPerc = filters.percentile ? parseFloat(filters.percentile) : null;
    const studentRank = filters.rank ? parseFloat(filters.rank) : null;
    const showRankColumn = filters.print_rank !== undefined ? filters.print_rank : true;

    const getAdmissionCategory = (item) => {
      if (studentRank) {
        const itemRank = parseFloat(item.Rank || item["AI Merit"]);
        if (!itemRank) return { label: "Target", colorClass: "target", color: "#d97706", bg: "#fffbeb", border: "#fde68a", lineBorder: "#fbbf24" };
        if (itemRank < studentRank) {
          return { label: "Dream", colorClass: "dream", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", lineBorder: "#c084fc" };
        } else if (itemRank >= studentRank && itemRank <= studentRank + 3000) {
          return { label: "Target", colorClass: "target", color: "#d97706", bg: "#fffbeb", border: "#fde68a", lineBorder: "#fbbf24" };
        } else {
          return { label: "Safe", colorClass: "safe", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", lineBorder: "#34d399" };
        }
      }
      
      if (studentPerc) {
        const itemPerc = parseFloat(item.Percentile || item["Percen Tile"] || 0);
        if (itemPerc > studentPerc) {
          return { label: "Dream", colorClass: "dream", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", lineBorder: "#c084fc" };
        } else if (itemPerc >= studentPerc - 2.0 && itemPerc <= studentPerc) {
          return { label: "Target", colorClass: "target", color: "#d97706", bg: "#fffbeb", border: "#fde68a", lineBorder: "#fbbf24" };
        } else {
          return { label: "Safe", colorClass: "safe", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", lineBorder: "#34d399" };
        }
      }
      
      return { label: "Target", colorClass: "target", color: "#d97706", bg: "#fffbeb", border: "#fde68a", lineBorder: "#fbbf24" };
    };

    const rankHeader = showRankColumn ? `<th style="width: 8%;">${rankLabel}</th>` : '';
    const instWidth = showRankColumn ? '32%' : '40%';
    const branchWidth = showRankColumn ? '20%' : '20%';

    const tableRows = prefs.map((p, i) => {
      const zone = getAdmissionCategory(p);
      const rankCell = showRankColumn ? `<td class="rank-cell">${listType === 'CET' ? (p["Rank"] || "-") : (p["AI Merit"] || "-")}</td>` : '';
      return `
        <tr>
          <td class="pref-num" style="text-align: center; border-left: 4px solid ${zone.lineBorder} !important; font-weight: 800;">${p.preference_number || i + 1}</td>
          <td class="inst-name">${p["INSTITUTE"] || p["Institute Name"] || ""}</td>
          <td>${p["BRANCH NAME"] || p["Course Name"] || ""}</td>
          <td>${p["CITY"] || ""}</td>
          ${rankCell}
          <td class="percentile-cell">${p["Percentile"] || p["Percen Tile"] || "-"}</td>
          <td>
            <span class="pdf-zone-badge ${zone.colorClass}" style="color: ${zone.color}; background: ${zone.bg}; border: 1px solid ${zone.border}; padding: 3px 8px; border-radius: 50px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block;">${zone.label}</span>
          </td>
          <td>
            <span class="choice-code">${p["Branch Code"] || p["Choice Code"] || "-"}</span>
          </td>
        </tr>
      `;
    }).join('');

    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Preference List - ${sn}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;700&display=swap');
          
          body {
            font-family: 'Outfit', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            padding: 30px;
            color: #1e293b;
            background: #fff;
            margin: 0;
            line-height: 1.4;
          }
          
          .brand-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #0284c7;
            padding-bottom: 15px;
            margin-bottom: 25px;
          }
          .brand-title {
            font-size: 24px;
            font-weight: 800;
            color: #0f172a;
            letter-spacing: -0.5px;
            margin: 0;
          }
          .brand-title span {
            color: #0284c7;
          }
          .brand-subtitle {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            color: #64748b;
            font-weight: 700;
            margin-top: 2px;
          }
          .doc-type {
            text-align: right;
          }
          .doc-type-badge {
            background: #e0f2fe;
            color: #0369a1;
            padding: 4px 12px;
            border-radius: 50px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            display: inline-block;
          }
          .doc-date {
            font-size: 12px;
            color: #64748b;
            margin-top: 5px;
            font-weight: 500;
          }
          
          .student-card {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 15px 20px;
            margin-bottom: 30px;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
          }
          .info-group {
            display: flex;
            flex-direction: column;
          }
          .info-label {
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #64748b;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .info-value {
            font-size: 14px;
            color: #0f172a;
            font-weight: 600;
          }
          .status-badge {
            display: inline-block;
            width: fit-content;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
          }
          .status-badge.draft {
            background: #fef3c7;
            color: #d97706;
          }
          .status-badge.finalized {
            background: #dcfce7;
            color: #15803d;
          }
          
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
            margin-top: 10px;
          }
          th {
            background: #0f172a;
            color: #fff;
            font-weight: 700;
            text-transform: uppercase;
            font-size: 10px;
            letter-spacing: 0.8px;
            padding: 12px 10px;
            text-align: left;
            border: none;
          }
          th:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }
          th:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
          td {
            padding: 10px;
            border-bottom: 1px solid #e2e8f0;
            vertical-align: middle;
            color: #334155;
          }
          tr:nth-child(even) td {
            background: #f8fafc;
          }
          
          .choice-code {
            font-family: 'JetBrains Mono', monospace;
            font-size: 12px;
            font-weight: 700;
            color: #0284c7;
            background: #f0f9ff;
            border: 1px dashed #bae6fd;
            padding: 4px 8px;
            border-radius: 6px;
            display: inline-block;
            letter-spacing: 0.5px;
          }
          
          .rank-cell, .percentile-cell {
            font-weight: 600;
            color: #0f172a;
          }
          .inst-name {
            font-weight: 600;
            color: #0f172a;
          }
          .pref-num {
            font-weight: 700;
            color: #475569;
          }
          
          tr {
            page-break-inside: avoid;
          }
          
          .print-footer {
            margin-top: 40px;
            border-top: 1px solid #e2e8f0;
            padding-top: 15px;
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            color: #94a3b8;
            font-weight: 500;
          }
        </style>
      </head>
      <body>
        
        <div class="brand-header">
          <div>
            <h1 class="brand-title">Admissions <span>Made Easy</span></h1>
            <div class="brand-subtitle">Smart Choice Entry Portal</div>
          </div>
          <div class="doc-type">
            <div class="doc-type-badge">${listType} Preference List</div>
            <div class="doc-date">${currentDate}</div>
          </div>
        </div>

        <div class="student-card">
          <div class="info-group">
            <div class="info-label">Student Name</div>
            <div class="info-value">${sn}</div>
          </div>
          <div class="info-group">
            <div class="info-label">Registration No</div>
            <div class="info-value">${rn}</div>
          </div>
          <div class="info-group">
            <div class="info-label">List Status</div>
            <div class="info-value">
              <span class="status-badge ${statusClass}">${statusText}</span>
            </div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 6%; text-align: center;">Pref #</th>
              <th style="width: ${instWidth};">Institute</th>
              <th style="width: ${branchWidth};">Branch / Course</th>
              <th style="width: 8%;">City</th>
              ${rankHeader}
              <th style="width: 8%;">Percentile</th>
              <th style="width: 10%;">Category</th>
              <th style="width: 8%;">Choice Code</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>

        <div class="print-footer">
          <div>Generated via Admissions Made Easy • Personal Student Preference Portal</div>
          <div>Page 1 of 1</div>
        </div>

      </body>
      </html>
    `;
    const w = window.open("", "_blank");
    w.document.write(printHTML);
    w.document.close();
    w.print();
  };

  const handleDownloadCSV = () => {
    if (!selectedDraft || !selectedDraft.preferences?.length) { toast.error("No data"); return; }
    const prefs = selectedDraft.preferences;
    const lt = selectedDraft.list_type;
    const headers = lt === "CET"
      ? ["Pref #","Institute","Branch","Category","City","Rank","Percentile","Choice Code"]
      : ["Pref #","Institute","Course","Course Group","City","AI Merit","Percentile","Choice Code"];
    const rows = prefs.map((p, i) => {
      if (lt === "CET") return [p.preference_number||i+1, p["INSTITUTE"]||"", p["BRANCH NAME"]||"", p["CATEGORY"]||"", p["CITY"]||"", p["Rank"]||"", p["Percentile"]||"", p["Branch Code"]||""];
      return [p.preference_number||i+1, p["Institute Name"]||"", p["Course Name"]||"", p["Course Group"]||"", p["CITY"]||"", p["AI Merit"]||"", p["Percen Tile"]||"", p["Choice Code"]||""];
    });
    let csv = headers.join(",") + "\n";
    rows.forEach(r => { csv += r.map(c => `"${c}"`).join(",") + "\n"; });
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `preference_list_${lt}_${Date.now()}.csv`; a.click();
    toast.success("Downloaded!");
  };

  const logout = () => { localStorage.removeItem(PREF_TOKEN_KEY); localStorage.removeItem(PREF_USER_KEY); navigate("/engineering-admission/portal-login"); };

  if (!user) return null;

  return (
    <>
      <Helmet><title>My Preference Lists | Preference Portal</title></Helmet>
      <style>{`
        .psp { min-height:100vh; background:#0f172a; color:#e2e8f0; font-family:'Inter',sans-serif; }
        .psp-top { display:flex; align-items:center; justify-content:space-between; padding:1rem 2rem; background:rgba(30,41,59,0.9); backdrop-filter:blur(12px); border-bottom:1px solid rgba(255,255,255,0.06); }
        .psp-top-left { display:flex; align-items:center; gap:0.75rem; }
        .psp-top-logo { width:40px; height:40px; border-radius:10px; background:linear-gradient(135deg,#0ea5e9,#0284c7); display:flex; align-items:center; justify-content:center; color:#fff; font-size:1rem; }
        .psp-top h2 { margin:0; font-size:1.1rem; color:#fff; font-weight:700; }
        .psp-top-right { display:flex; align-items:center; gap:0.6rem; }
        .psp-chip { padding:0.35rem 0.85rem; border-radius:50px; font-size:0.78rem; font-weight:600; background:rgba(14,165,233,0.1); border:1px solid rgba(14,165,233,0.25); color:#38bdf8; }
        .psp-logout { padding:0.35rem 0.85rem; background:rgba(239,68,68,0.1); border:1px solid rgba(239,68,68,0.2); border-radius:6px; color:#f87171; cursor:pointer; font-size:0.78rem; font-weight:600; font-family:inherit; }
        .psp-body { max-width:900px; margin:0 auto; padding:2rem; }
        .psp-tabs { display:flex; gap:0.5rem; margin-bottom:1.5rem; }
        .psp-tab { padding:0.6rem 1.5rem; border-radius:10px; background:rgba(255,255,255,0.03); border:1.5px solid rgba(255,255,255,0.08); color:#64748b; cursor:pointer; font-weight:600; font-size:0.85rem; transition:all 0.2s; font-family:inherit; }
        .psp-tab.active { background:rgba(14,165,233,0.12); border-color:#0ea5e9; color:#38bdf8; }
        .psp-card { background:rgba(30,41,59,0.6); border:1px solid rgba(255,255,255,0.06); border-radius:16px; padding:1.5rem; margin-bottom:1rem; }
        .psp-card-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1rem; flex-wrap:wrap; gap:0.5rem; }
        .psp-card-header h3 { margin:0; font-size:1rem; color:#fff; font-weight:700; }
        .psp-status { padding:0.25rem 0.75rem; border-radius:50px; font-size:0.72rem; font-weight:700; }
        .psp-status.draft { background:rgba(245,158,11,0.12); color:#fbbf24; }
        .psp-status.final { background:rgba(16,185,129,0.12); color:#34d399; }
        .psp-actions { display:flex; gap:0.5rem; }
        .psp-action-btn { padding:0.4rem 1rem; border-radius:8px; border:none; cursor:pointer; font-weight:600; font-size:0.8rem; font-family:inherit; transition:all 0.15s; }
        .psp-action-btn.dl { background:rgba(14,165,233,0.1); color:#38bdf8; border:1px solid rgba(14,165,233,0.25); }
        .psp-action-btn.dl:hover { background:rgba(14,165,233,0.2); }
        .psp-action-btn.print { background:rgba(168,85,247,0.1); color:#c084fc; border:1px solid rgba(168,85,247,0.25); }
        .psp-action-btn.print:hover { background:rgba(168,85,247,0.2); }
        .psp-table { width:100%; border-collapse:collapse; font-size:0.82rem; }
        .psp-table th { padding:0.5rem; text-align:left; color:#64748b; font-size:0.7rem; text-transform:uppercase; letter-spacing:1px; font-weight:700; border-bottom:1px solid rgba(255,255,255,0.06); }
        .psp-table td { padding:0.5rem; border-bottom:1px solid rgba(255,255,255,0.03); }
        .psp-empty { text-align:center; padding:4rem 2rem; color:#475569; }
        .psp-empty i { font-size:3rem; margin-bottom:1rem; display:block; }
      `}</style>

      <div className="psp">
        <div className="psp-top">
          <div className="psp-top-left">
            <div className="psp-top-logo"><i className="fas fa-graduation-cap"></i></div>
            <h2>My Preference Lists</h2>
          </div>
          <div className="psp-top-right">
            <span className="psp-chip"><i className="fas fa-user" style={{marginRight:4}}></i>{user.name}</span>
            <button className="psp-logout" onClick={logout}>Logout</button>
          </div>
        </div>

        <div className="psp-body">
          {loading ? (
            <div className="psp-empty"><i className="fas fa-spinner fa-spin"></i><p>Loading your lists...</p></div>
          ) : drafts.length === 0 ? (
            <div className="psp-empty">
              <i className="fas fa-inbox"></i>
              <p style={{fontWeight:600,fontSize:'1.1rem',color:'#94a3b8'}}>No Preference Lists Yet</p>
              <p>Your counselor will create and share your preference lists here.</p>
            </div>
          ) : (
            <>
              <div className="psp-tabs">
                {drafts.map((d, i) => (
                  <button key={i} className={`psp-tab ${selectedDraft === d ? 'active' : ''}`} onClick={() => setSelectedDraft(d)}>
                    <i className={`fas ${d.list_type==='CET'?'fa-map-marked-alt':'fa-globe'}`} style={{marginRight:6}}></i>
                    {d.list_type} List
                  </button>
                ))}
              </div>

              {selectedDraft && (
                <div className="psp-card">
                  <div className="psp-card-header">
                    <div>
                      <h3>{selectedDraft.list_type} Preference List</h3>
                      <span style={{fontSize:'0.78rem',color:'#64748b'}}>
                        {selectedDraft.preferences?.length || 0} colleges • Updated {new Date(selectedDraft.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
                      <span className={`psp-status ${selectedDraft.status==='FINALIZED'?'final':'draft'}`}>
                        {selectedDraft.status}
                      </span>
                      <div className="psp-actions">
                        <button className="psp-action-btn dl" onClick={handleDownloadCSV}><i className="fas fa-download" style={{marginRight:4}}></i>Download</button>
                        <button className="psp-action-btn print" onClick={handlePrint}><i className="fas fa-print" style={{marginRight:4}}></i>Print</button>
                      </div>
                    </div>
                  </div>

                  <table className="psp-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Institute</th>
                        <th>{selectedDraft.list_type==='CET'?'Branch':'Course'}</th>
                        <th>City</th>
                        <th>{selectedDraft.list_type==='CET'?'Rank':'AI Merit'}</th>
                        <th>Percentile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedDraft.preferences || []).map((p, i) => (
                        <tr key={i}>
                          <td style={{fontWeight:700,color:'#38bdf8',whiteSpace:'nowrap'}}>{p.preference_number || i+1}</td>
                          <td style={{fontWeight:600,color:'#f1f5f9'}}>{p["INSTITUTE"]||p["Institute Name"]||""}</td>
                          <td>{p["BRANCH NAME"]||p["Course Name"]||""}</td>
                          <td>{p["CITY"]||""}</td>
                          <td style={{fontFamily:'monospace'}}>{selectedDraft.list_type==='CET'?(p["Rank"]||""):(p["AI Merit"]||"")}</td>
                          <td style={{fontFamily:'monospace'}}>{p["Percentile"]||p["Percen Tile"]||""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
